<script setup>
import { ref, computed } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import { tourCategories } from '@/data/content.js'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  tour:      { type: Object, required: true },
  base_path: { type: String, default: '/tours' },
})

const page = usePage()
const inquirePath = computed(() => page.props.inquire_path || '/inquire')

const photos = props.tour.photo_urls?.length ? props.tour.photo_urls : []
const activeIdx = ref(0)

const durationLabel = computed(() => {
  const m = props.tour.duration_minutes
  if (!m) return null
  const h = Math.round(m / 60)
  return h >= 8 ? 'Full day' : h <= 1 ? `${m} min` : `${h} hours`
})
const chatIntents = computed(() => props.tour.contact_intents || {})

const form = useForm({ name: '', email: '', phone: '', date: '', group: '', message: '', topic: 'tour', website: '' })

function submit() {
  form
    .transform((d) => ({
      inquiry: {
        name: d.name, email: d.email, phone: d.phone, topic: 'tour', website: d.website,
        message: `Request to book: ${props.tour.title}\nPreferred date: ${d.date || '—'}\nGroup size: ${d.group || '—'}\n\n${d.message}`,
      },
    }))
    .post(inquirePath.value, { preserveScroll: true })
}
function platformLabel(p) { return p.charAt(0).toUpperCase() + p.slice(1) }
</script>

<template>
  <Head>
    <title>{{ tour.title }} · Kenya2Go</title>
    <meta name="description" :content="tour.summary || tour.description">
  </Head>

  <div class="kp">
    <section class="show">
      <a :href="base_path" class="show__back">← All experiences</a>

      <header class="show__head">
        <p v-if="tour.category" class="kp-eyebrow">{{ tourCategories[tour.category] || tour.category }}</p>
        <h1 class="show__title">{{ tour.title }}</h1>
        <p v-if="tour.summary" class="show__summary">{{ tour.summary }}</p>
      </header>

      <div class="gallery">
        <div class="gallery__main">
          <img v-if="photos[activeIdx]" :src="photos[activeIdx]" :alt="tour.title">
          <CatalogPlaceholder v-else kind="tour" :category="tour.category" />
        </div>
        <ul v-if="photos.length > 1" class="thumbs">
          <li v-for="(p, i) in photos" :key="p">
            <button :class="['thumb', i === activeIdx && 'is-active']" @click="activeIdx = i"><img :src="p" alt=""></button>
          </li>
        </ul>
      </div>

      <div class="show__layout">
        <div class="show__main">
          <div class="block">
            <h2 class="block__h">About this experience</h2>
            <p class="kp-prose show__desc">{{ tour.description }}</p>
          </div>
          <div v-if="tour.inclusions" class="block">
            <h2 class="block__h">What’s included</h2>
            <p class="kp-prose show__desc">{{ tour.inclusions }}</p>
          </div>
          <div class="block">
            <h2 class="block__h">Good to know</h2>
            <dl class="facts">
              <div v-if="durationLabel"><dt>Duration</dt><dd>{{ durationLabel }}</dd></div>
              <div v-if="tour.max_group_size"><dt>Group size</dt><dd>Max {{ tour.max_group_size }}</dd></div>
              <div v-if="tour.languages_offered"><dt>Languages</dt><dd>{{ tour.languages_offered }}</dd></div>
              <div v-if="tour.meeting_point_address"><dt>Meeting point</dt><dd>{{ tour.meeting_point_address }}</dd></div>
              <div v-if="tour.meeting_point_description"><dt>Details</dt><dd>{{ tour.meeting_point_description }}</dd></div>
            </dl>
          </div>
        </div>

        <aside class="show__side">
          <div class="book">
            <div class="book__price">
              <span class="book__from">from</span>
              <span class="book__amount">{{ tour.price_adult }}</span>
              <span class="book__unit">per adult</span>
            </div>

            <form class="book__form" @submit.prevent="submit">
              <input v-model="form.name" type="text" placeholder="Your name" autocomplete="name">
              <input v-model="form.email" type="email" placeholder="Email" autocomplete="email">
              <input v-model="form.phone" type="tel" placeholder="Phone / WhatsApp" autocomplete="tel">
              <div class="book__row">
                <input v-model="form.date" type="date" aria-label="Preferred date">
                <input v-model="form.group" type="number" min="1" placeholder="People" aria-label="Group size">
              </div>
              <textarea v-model="form.message" rows="3" placeholder="Anything we should know?"></textarea>
              <input v-model="form.website" type="text" tabindex="-1" class="hp" aria-hidden="true">
              <button type="submit" class="kp-btn kp-btn--solid book__submit" :disabled="form.processing">
                {{ form.processing ? 'Sending…' : 'Request to book' }}
              </button>
              <p class="book__note">No payment now — we confirm availability with the operator first.</p>
            </form>

            <div v-if="Object.keys(chatIntents).length" class="book__chat">
              <span>or message us</span>
              <div class="book__chatbtns">
                <a v-for="(url, platform) in chatIntents" :key="platform" :href="url" target="_blank" rel="noopener" class="chatbtn">
                  {{ platformLabel(platform) }}
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  </div>
</template>

<style scoped>
.show { max-width: 70rem; margin: 0 auto; padding: 2.5rem 1.5rem 4rem; }
.show__back { color: var(--muted); text-decoration: none; font-size: 0.85rem; font-weight: 500; }
.show__head { margin: 1.2rem 0 1.5rem; }
.show__title { font-family: var(--display); font-weight: 400; font-size: clamp(2rem, 4.5vw, 3rem); line-height: 1.04; letter-spacing: -0.02em; margin: 0.3rem 0 0.6rem; color: var(--ink); }
.show__summary { font-size: 1.1rem; line-height: 1.6; color: var(--inkSoft); margin: 0; max-width: 44rem; }

.gallery__main { aspect-ratio: 16 / 9; background: var(--surfaceAlt); border-radius: var(--radius-lg); overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }

.show__layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 3rem; margin-top: 2.5rem; }
.show__main { display: grid; gap: 2rem; }
.block__h { font-family: var(--display); font-weight: 500; font-size: 1.4rem; margin: 0 0 0.8rem; color: var(--ink); }
.show__desc { white-space: pre-wrap; }
.facts { display: grid; gap: 0.6rem; margin: 0; }
.facts > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; padding-bottom: 0.6rem; border-bottom: 1px solid var(--line); }
.facts dt { color: var(--muted); font-size: 0.88rem; }
.facts dd { margin: 0; color: var(--ink); font-size: 0.95rem; }

.show__side { align-self: start; position: sticky; top: 1.5rem; }
.book { background: var(--surface); border: 1px solid var(--lineStrong); border-radius: var(--radius-lg); padding: 1.5rem; box-shadow: var(--shadow-md); }
.book__price { display: flex; align-items: baseline; gap: 0.4rem; margin-bottom: 1.2rem; flex-wrap: wrap; }
.book__from { color: var(--muted); font-size: 0.85rem; }
.book__amount { font-family: var(--display); font-weight: 600; font-size: 1.8rem; color: var(--ink); }
.book__unit { color: var(--muted); font-size: 0.85rem; }
.book__form { display: flex; flex-direction: column; gap: 0.6rem; }
.book__row { display: grid; grid-template-columns: 1fr 5.5rem; gap: 0.6rem; }
.book__form input, .book__form textarea { padding: 0.65rem 0.8rem; border: 1px solid var(--lineStrong); background: var(--bg); color: var(--ink); font: inherit; font-size: 0.92rem; }
.hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.book__submit { width: 100%; margin-top: 0.3rem; }
.book__note { font-size: 0.78rem; color: var(--muted); text-align: center; margin: 0; }
.book__chat { margin-top: 1.2rem; padding-top: 1.2rem; border-top: 1px solid var(--line); text-align: center; }
.book__chat > span { font-size: 0.82rem; color: var(--muted); }
.book__chatbtns { display: flex; gap: 0.5rem; justify-content: center; margin-top: 0.6rem; }
.chatbtn { padding: 0.5rem 1rem; background: var(--accentSoft); color: var(--accent); border-radius: 999px; font-weight: 600; font-size: 0.85rem; text-decoration: none; }

@media (max-width: 820px) {
  .show__layout { grid-template-columns: 1fr; gap: 2rem; }
  .show__side { position: static; }
}
</style>
